import CircularFilledIcon from "@/components/CircularButton/styled/CircularFilledIcon";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { Editor, EditorContent, useEditor } from "@knowt/editor/react";
import { ArrowUp } from "lucide-react";
import { useChatEditorCommunicator } from "../hooks/useChatEditorCommunicator";
import { extensionKit } from "@/components/FullPageEditor/extensions";
import toast from "react-hot-toast";
import { useS3UploadJobsSelector } from "@knowt/syncing/context/S3UploadJobContext";
import {
    RichEditorAttachmentList,
    RichEditorResponseToolbar,
} from "@/components/RichResponseEditor/RichResponseEditor";
import { LocalMessageAttachment } from "@knowt/syncing/types/common";
import useCombinedState from "@knowt/syncing/utils/hooks/useCombinedState";
import LazyLoaded from "@/components/LazyLoaded";
import dynamic from "next/dynamic";
import { editorInsertMathExpression } from "@/components/FullPageEditor/utils/mathematics";
import { useEffect, useState } from "react";

const MathFieldInputPopup = dynamic(() => import("@/components/MathField/MathFieldInputPopup"), { ssr: false });

//TODO: find a better place for this to be reused in different features
const USER_ATTACHMENTS_BUCKET = "knowt-user-attachments";

const ChatEditor = ({
    id,
    placeholder,
    inputContainerRef,
    disabled,
    disabledReason,
}: {
    id?: string;
    placeholder: string;
    inputContainerRef: React.RefObject<HTMLDivElement | null>;
    disabled?: boolean;
    disabledReason?: string;
}) => {
    const { isUploading, confirmUpload } = useS3UploadJobsSelector(s => s);

    const chatInputRef = useChatEditorCommunicator(s => s.chatInputRef);
    const inputQuestion = useChatEditorCommunicator(s => s.inputQuestion);
    const isStreaming = useChatEditorCommunicator(s => s.isStreaming);
    const isWaitingForResponse = useChatEditorCommunicator(s => s.isWaitingForResponse);
    const selectedContent = useChatEditorCommunicator(s => s.selectedContent);
    const prompt = useChatEditorCommunicator(s => s.prompt);
    const localEditorAttachments = useChatEditorCommunicator(s => s.localEditorAttachments);
    const chosenSuggestion = useChatEditorCommunicator(s => s.chosenSuggestion);

    const handleUnselect = useChatEditorCommunicator(s => s.handleUnselect);
    const stagePrompt = useChatEditorCommunicator(s => s.stagePrompt);
    const onSend = useChatEditorCommunicator(s => s.processChat);
    const setLocalEditorAttachments = useChatEditorCommunicator(s => s.setLocalEditorAttachments);

    const inputContainerHeight = inputContainerRef.current?.clientHeight || 0;
    const heightWhenInputHasMoreThanTwoLines = 50;
    const hasInputSurpassedTwoLines = inputContainerHeight > heightWhenInputHasMoreThanTwoLines;

    const [isSendButtonHidden, setIsSendButtonHidden] = useState(false);

    const [editorMathPopupStates, setEditorMathPopupStates] = useCombinedState<{
        isOpen: boolean;
        content: string;
        editor: Editor | null;
        editorPos: { start: number; end: number };
    }>({ isOpen: false, content: "", editor: null, editorPos: { start: 0, end: 0 } });

    const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && !e.shiftKey && !editor?.isActive("codeBlock")) {
            e.preventDefault();
            editor?.commands.clearContent();
            await handleChat();
        }
        if (e.key === "Backspace" && !inputQuestion.length) {
            handleUnselect();
            stagePrompt({ inputQuestion: "", prompt: "" });
        }
    };

    const handleChat = async () => {
        if (disabled) return toast.error(disabledReason || "Chat is disabled");

        if (isStreaming || !inputQuestion.length || isWaitingForResponse) return;

        await onSend({
            inputQuestion,
            prompt,
            quote: selectedContent,
        });

        stagePrompt({
            inputQuestion: "",
            prompt: "",
        });

        editor?.commands.clearContent();
    };

    const handleChange = (content: string | null) => {
        if (!content) {
            stagePrompt({ inputQuestion: "", prompt: "" });
            return;
        }

        stagePrompt({ inputQuestion: content });
    };

    const editor = useEditor({
        content: inputQuestion,
        editable: true,
        immediatelyRender: false,
        extensions: extensionKit({
            editable: true,
            placeholderOptions: { placeholder },
            copilotProps: {
                disabled: true,
            },
            onKatexClick: (_e, { content, pos, editor }) => {
                if (pos.start && pos.end) {
                    setEditorMathPopupStates({ isOpen: true, editorPos: pos, content, editor });
                }
            },
        }),
        onUpdate: ({ editor }) => {
            const content = editor.getText() ? editor.getHTML() : "";
            handleChange(content);
        },
    });

    const handleUpdateAttachments = (attachments: LocalMessageAttachment[]) => {
        //Any extra work to do with attachments goes here
        setLocalEditorAttachments(attachments);
    };

    useEffect(() => {
        if (chosenSuggestion) {
            editor?.commands.setContent(chosenSuggestion);
        }
    }, [chosenSuggestion]);

    return (
        <FlexColumn
            style={{
                height: "fit-content",
                gap: spacing.XS,
            }}>
            <RichEditorAttachmentList
                isUploading={isUploading}
                localEditorAttachments={localEditorAttachments}
                handleUpdateAttachments={handleUpdateAttachments}
            />
            <EditorContent id={id} ref={chatInputRef} editor={editor} onKeyDown={handleKeyDown} style={{ flex: 1 }} />
            <FlexRowAlignCenter
                style={{
                    width: "100%",
                    justifyContent: hasInputSurpassedTwoLines ? "flex-end" : "center",
                    gap: spacing.XS,
                }}>
                <RichEditorResponseToolbar
                    editor={editor}
                    localEditorAttachments={localEditorAttachments}
                    handleUpdateAttachments={handleUpdateAttachments}
                    confirmFileUpload={confirmUpload}
                    filesS3BucketName={USER_ATTACHMENTS_BUCKET}
                    stretchToolbar={setIsSendButtonHidden}
                />
                {!isSendButtonHidden && (
                    <CircularFilledIcon
                        Icon={ArrowUp}
                        onClick={handleChat}
                        primaryBg={themeColors.neutralBlack}
                        primaryColor={themeColors.neutralWhite}
                        disabled={isStreaming || !inputQuestion || isWaitingForResponse}
                        sx={{
                            marginLeft: "1rem",
                            opacity: isStreaming || !inputQuestion || isWaitingForResponse ? 0.5 : 1,
                        }}
                        radius="3rem"
                    />
                )}
            </FlexRowAlignCenter>
            <LazyLoaded load={editorMathPopupStates.isOpen}>
                <MathFieldInputPopup
                    isOpen={editorMathPopupStates.isOpen}
                    onClose={() =>
                        setEditorMathPopupStates({
                            isOpen: false,
                            content: "",
                            editor: null,
                            editorPos: { start: 0, end: 0 },
                        })
                    }
                    onInsert={value => {
                        const { editor, editorPos } = editorMathPopupStates;
                        if (!editor || !editorPos) return;
                        editorInsertMathExpression({ editor, pos: editorPos, expression: value });
                    }}
                    selectedLatex={editorMathPopupStates.content}
                />
            </LazyLoaded>
        </FlexColumn>
    );
};

export default ChatEditor;
